/**
 * Webtech HR Engine™ - User Profile Page
 * Page de profil utilisateur avec changement de mot de passe
 */

import { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  User, 
  Mail, 
  Building2, 
  Shield, 
  Key, 
  Loader2, 
  CheckCircle2,
  Eye,
  EyeOff,
  AlertTriangle
} from 'lucide-react';

const ROLE_LABELS = {
  'SuperAdmin': 'Super Administrateur',
  'Admin': 'Admin RH',
  'Manager': 'Manager',
  'Employé': 'Employé'
};

const ROLE_COLORS = {
  'SuperAdmin': 'bg-purple-100 text-purple-700',
  'Admin': 'bg-blue-100 text-blue-700',
  'Manager': 'bg-green-100 text-green-700',
  'Employé': 'bg-slate-100 text-slate-700'
};

export default function ProfilePage() {
  const { user } = useAuth();
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);

  const validatePassword = () => {
    if (!passwordForm.currentPassword) {
      toast.error('Veuillez entrer votre mot de passe actuel');
      return false;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return false;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return false;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error('Le nouveau mot de passe doit être différent de l\'ancien');
      return false;
    }
    return true;
  };

  const handleChangePassword = async () => {
    setConfirmDialog(false);
    setSaving(true);
    
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      toast.success('Mot de passe modifié avec succès');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validatePassword()) {
      setConfirmDialog(true);
    }
  };

  return (
    <DashboardLayout title="Mon profil">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Profile Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-webtech-blue" />
              Informations du profil
            </CardTitle>
            <CardDescription>
              Vos informations personnelles et de connexion
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar and Name */}
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-webtech-blue flex items-center justify-center text-white text-2xl font-semibold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {user?.firstName} {user?.lastName}
                </h2>
                <Badge className={ROLE_COLORS[user?.role] || ROLE_COLORS['Employé']}>
                  {ROLE_LABELS[user?.role] || user?.role}
                </Badge>
              </div>
            </div>
            
            <Separator />
            
            {/* Info Grid */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-slate-500 text-xs uppercase tracking-wide">Email</Label>
                <div className="flex items-center gap-2 text-slate-900">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {user?.email}
                </div>
              </div>
              
              {user?.companyId && (
                <div className="space-y-1">
                  <Label className="text-slate-500 text-xs uppercase tracking-wide">Entreprise</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    {user?.companyName || 'Non défini'}
                  </div>
                </div>
              )}
              
              <div className="space-y-1">
                <Label className="text-slate-500 text-xs uppercase tracking-wide">Rôle</Label>
                <div className="flex items-center gap-2 text-slate-900">
                  <Shield className="h-4 w-4 text-slate-400" />
                  {ROLE_LABELS[user?.role] || user?.role}
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-slate-500 text-xs uppercase tracking-wide">ID utilisateur</Label>
                <div className="flex items-center gap-2 text-slate-500 text-sm font-mono">
                  {user?.id?.substring(0, 8)}...
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-webtech-blue" />
              Changer mon mot de passe
            </CardTitle>
            <CardDescription>
              Modifiez votre mot de passe de connexion
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Minimum 8 caractères</p>
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Les mots de passe ne correspondent pas
                  </p>
                )}
                {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.newPassword.length >= 8 && (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Les mots de passe correspondent
                  </p>
                )}
              </div>
              
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Changer mon mot de passe
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Confirm Dialog */}
        <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer le changement de mot de passe</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir changer votre mot de passe ? 
                Vous devrez utiliser le nouveau mot de passe lors de votre prochaine connexion.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleChangePassword}
                className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
              >
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
